import React, { FC, MouseEvent, useCallback, memo } from 'react';
import styled, { css } from 'styled-components';
import Checkbox from '@minecraft.minusable-checkbox';
import UncontrolledArrow from '@minecraft.uncontrolled-arrow';
import { SelectOption } from '@minecraft.typings';
import { helpers } from '@minecraft.utils';
import { DropdownWithChildrenProps } from '../shared/types';

const { getAllNestedOptions } = helpers;

export const StyledDropdownItem = styled.div<{
  $isItemSelected?: boolean;
  $isItemDisabled?: boolean;
  $isMultipleSelect?: boolean;
  $nestingLevel: number;
}>`
  background-color: ${(props) =>
    props.$isItemSelected ? props.theme.form.option.state.selected.backgroundColor : 'transparent'};
  border: 0;
  font-size: 0.875rem;
  text-align: inherit;
  width: 100%;

  ${(props) =>
    !props.$isItemDisabled &&
    css`
      cursor: ${props.$isMultipleSelect ? 'default' : 'pointer'};
    `}

  ${(props) =>
    props.$nestingLevel === 1 &&
    `
    font-weight: 600;
  `}
  ${(props) =>
    props.$nestingLevel === 2 &&
    `
    font-weight: 500;
  `}
`;

const StyledDropdownItemInner = styled.div`
  display: flex;
  padding: 0.5rem;
  justify-content: space-between;
`;

const StyledDropdownItemInnerLabel = styled.span`
  white-space: pre-wrap;
`;

const StyledCheckbox = styled(Checkbox)`
  margin: 0;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
`;

const StyledUncontrolledArrow = styled(UncontrolledArrow)`
  margin-left: 1rem;
  height: 1rem;
  width: 1rem;
`;

interface Props extends Partial<DropdownWithChildrenProps> {
  isItemSelected: boolean;
  isItemDisabled: boolean;
  isItemCountExist: boolean;
  areSubOptionsExist: boolean;
  checkboxEnable: boolean;
  minusEnable: boolean;
  nestingLevel: number;
  item: SelectOption;
  toggleParentOptions: (parentItem: SelectOption) => void;
  onDropdownItemSelect: (newItems: SelectOption[]) => void;
  className?: string;
}

const DropdownWithChildrenDropdownItem: FC<Props> = ({
  isMultipleSelect,
  isCollapsable,
  isItemSelected,
  isItemDisabled,
  isItemCountExist,
  areSubOptionsExist,
  checkboxEnable,
  nestingLevel,
  minusEnable,
  item,
  toggleParentOptions,
  onDropdownItemSelect,
  className,
  dataTestId,
}) => {
  const handleItemClick = useCallback(
    (event: MouseEvent<HTMLDivElement>): void => {
      if (isItemDisabled || (isMultipleSelect && !checkboxEnable) || (!isMultipleSelect && areSubOptionsExist)) {
        return;
      }

      const getNewItems = () => {
        if (!isMultipleSelect && !areSubOptionsExist) {
          return [item];
        }

        return areSubOptionsExist ? getAllNestedOptions(item.options) : [item];
      };

      event.preventDefault();

      onDropdownItemSelect(getNewItems());
    },
    [isMultipleSelect, isItemDisabled, areSubOptionsExist, checkboxEnable, item, onDropdownItemSelect]
  );

  const handleArrowClick = useCallback(() => {
    toggleParentOptions(item);
  }, [item, toggleParentOptions]);

  const renderLabel = useCallback((): JSX.Element => {
    return (
      <StyledDropdownItemInnerLabel data-testid={`option-${item.label}`} data-qa-id={`option-${item.value}`}>
        {item.label}
        {isItemCountExist ? ` (${item.count})` : ''}
      </StyledDropdownItemInnerLabel>
    );
  }, [isItemCountExist, item]);

  return (
    <StyledDropdownItem
      $isMultipleSelect={isMultipleSelect && checkboxEnable}
      $isItemSelected={
        (isMultipleSelect && isItemSelected && checkboxEnable) ||
        (!isMultipleSelect && isItemSelected && !areSubOptionsExist)
      }
      $isItemDisabled={isItemDisabled}
      $nestingLevel={item?.options && nestingLevel}
      onClick={handleItemClick}
      className={className}
      data-qa-id={!item?.options ? 'dropdown-sub-item' : 'dropdown-title-item'}
      data-testid="dropdown-item-wrapper"
    >
      <StyledDropdownItemInner
        className="dropdown-item-inner"
        data-qa-id={`${item.value?.toString().toLocaleLowerCase()}-option`}
      >
        {isMultipleSelect && checkboxEnable ? (
          <StyledCheckbox
            isChecked={isItemSelected}
            minusEnable={minusEnable}
            disabled={isItemDisabled}
            data-qa-id={item.value?.toString()}
            data-testid={`${dataTestId}-item-${item.value}`}
            readOnly
            onChange={() => undefined}
          >
            {renderLabel()}
          </StyledCheckbox>
        ) : (
          renderLabel()
        )}
        {isCollapsable && areSubOptionsExist && nestingLevel === 1 && (
          <StyledUncontrolledArrow onClick={handleArrowClick} />
        )}
      </StyledDropdownItemInner>
    </StyledDropdownItem>
  );
};

export default memo(DropdownWithChildrenDropdownItem);
